/**
 * KWC header v2.
 *
 * Progressive enhancement only. Without this file the header still renders,
 * every link navigates, and dropdowns open via :focus-within (see the CSS), so
 * a JS failure never traps a shopper.
 *
 * Search and cart are NOT handled here: those buttons carry the theme's own
 * declarative on:click bindings (#search-modal/showDialog, #cart-drawer/toggle)
 * and the cart count comes from the theme's <cart-icon> component.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DESKTOP = window.matchMedia('(min-width: 990px)');
  var OPEN_DELAY = 100;
  var CLOSE_DELAY = 200;

  function initHeader(root) {
    /* ---------------- Dropdowns ---------------- */
    var items = Array.prototype.slice.call(
      root.querySelectorAll('.kwc-hd__nav-item--has-panel')
    );
    var openTimer = null;
    var closeTimer = null;

    function closeAll(except) {
      items.forEach(function (item) {
        if (item === except) return;
        item.classList.remove('is-open');
        var trigger = item.querySelector('[data-kwc-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    function open(item) {
      closeAll(item);
      item.classList.add('is-open');
      var trigger = item.querySelector('[data-kwc-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    function close(item) {
      item.classList.remove('is-open');
      var trigger = item.querySelector('[data-kwc-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-kwc-trigger]');
      if (!trigger) return;

      // Hover with intent delays, desktop only.
      item.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'touch' || !DESKTOP.matches) return;
        clearTimeout(closeTimer);
        openTimer = setTimeout(function () { open(item); }, OPEN_DELAY);
      });

      item.addEventListener('pointerleave', function (event) {
        if (event.pointerType === 'touch' || !DESKTOP.matches) return;
        clearTimeout(openTimer);
        closeTimer = setTimeout(function () { close(item); }, CLOSE_DELAY);
      });

      // Click / Enter / Space. The trigger is a <button>, so the browser fires
      // click for both keys already.
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        if (item.classList.contains('is-open')) close(item);
        else open(item);
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAll(null);
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) closeAll(null);
    });

    root.addEventListener('focusout', function () {
      // Defer so the incoming focus target is known.
      setTimeout(function () {
        if (!root.contains(document.activeElement)) closeAll(null);
      }, 0);
    });

    /* ---------------- Sticky (observer, never a scroll listener) -------- */
    var main = root.querySelector('[data-kwc-main]');
    var sentinel = root.querySelector('[data-kwc-sentinel]');

    if (main && sentinel && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            main.classList.toggle('is-stuck', !entry.isIntersecting);
          });
        },
        { rootMargin: '0px', threshold: 0 }
      ).observe(sentinel);
    }

    /* ---------------- Rotating offer messages --------------------------- */
    var messages = Array.prototype.slice.call(root.querySelectorAll('[data-kwc-message]'));

    if (messages.length > 1 && !REDUCED.matches) {
      var current = 0;
      var rotateMs = parseInt(root.getAttribute('data-rotate-ms'), 10) || 4000;
      var rotateTimer = null;
      var wrap = root.querySelector('[data-kwc-messages]');

      function tick() {
        messages[current].classList.remove('is-active');
        current = (current + 1) % messages.length;
        messages[current].classList.add('is-active');
      }

      function startRotation() {
        stopRotation();
        rotateTimer = setInterval(tick, rotateMs);
      }

      function stopRotation() {
        if (rotateTimer) {
          clearInterval(rotateTimer);
          rotateTimer = null;
        }
      }

      if (wrap) {
        wrap.addEventListener('pointerenter', stopRotation);
        wrap.addEventListener('pointerleave', startRotation);
      }

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stopRotation();
        else startRotation();
      });

      startRotation();
    }

    /* ---------------- Mobile drawer ------------------------------------- */
    var burger = root.querySelector('[data-kwc-burger]');
    var drawer = root.querySelector('[data-kwc-drawer]');
    var overlay = root.querySelector('[data-kwc-overlay]');
    var closeBtn = root.querySelector('[data-kwc-close]');
    var lastFocused = null;

    function focusables() {
      return Array.prototype.slice
        .call(drawer.querySelectorAll('a[href], button:not([disabled])'))
        .filter(function (el) { return el.offsetParent !== null; });
    }

    function openDrawer() {
      if (!drawer || !overlay) return;
      lastFocused = document.activeElement;
      drawer.hidden = false;
      overlay.hidden = false;
      // Next frame, so the translate transition actually runs.
      requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      if (burger) burger.setAttribute('aria-expanded', 'true');
      var first = focusables()[0];
      if (first) first.focus();
    }

    function closeDrawer() {
      if (!drawer || !overlay) return;
      drawer.classList.remove('is-open');
      overlay.hidden = true;
      document.body.style.overflow = '';
      if (burger) burger.setAttribute('aria-expanded', 'false');
      setTimeout(function () { drawer.hidden = true; }, 250);
      if (lastFocused) lastFocused.focus();
    }

    if (burger) burger.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    if (drawer) {
      drawer.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          closeDrawer();
          return;
        }
        if (event.key !== 'Tab') return;

        // Focus trap.
        var list = focusables();
        if (list.length === 0) return;
        var first = list[0];
        var last = list[list.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    // Drawer accordions.
    root.querySelectorAll('[data-kwc-accordion]').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        var panel = toggle.nextElementSibling;
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel) panel.hidden = expanded;
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-kwc-header]').forEach(initHeader);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-kwc-header]');
    if (root) initHeader(root);
  });
})();
